import {calculateWidthSecteur} from './calculateWidthSecteur'
import {getWidthAtPK} from './getWidthAtPK'
import {getGridCell} from './getGridCell'
import {TSwitch} from '../types/project.type'
import type {TAxisStretch} from './axisStretch'

/**
 * Axis direction: ascending (kmStart → kmEnd) or descending (kmEnd → kmStart)
 */
export type TAxisDirection = 'ASC' | 'DESC'

/**
 * Axis conversion system between railway kilometric coordinates (PK) and pixel positions.
 * Used throughout the app to position and size visual elements.
 * See [README.md](./README.md) for detailed documentation.
 */
export type Axis = {
  /**
   * Axis direction: ascending (kmStart → kmEnd) or descending (kmEnd → kmStart)
   */
  readonly direction: TAxisDirection

  /**
   * Starting kilometer of the project
   */
  kmStart: number

  /**
   * Ending kilometer of the project
   */
  kmEnd: number

  /**
   * Sampling value of the project (space between km points)
   */
  echantillonValue: number

  /**
   * Switches of the project
   */
  switches: TSwitch[]

  /**
   * How many PK blocks this axis is laid over. 1 for a project axis.
   *
   * Above 1 the scalar fields (`kmStart`, `kmEnd`, `direction`) describe the
   * chain's envelope rather than a single continuous scale, so the edit paths —
   * drag, resize, km validation, form defaults — must switch off instead of
   * reading them. See `createCompositeAxis`.
   */
  blockCount?: number

  /**
   * Total project width in pixels
   */
  totalWidth: () => number

  /**
   * Pixel width of a kilometer interval
   */
  width: (startKm: number, endKm: number) => number

  /**
   * Left position in pixels of an interval
   */
  left: (startKm: number, endKm: number) => number

  /**
   * Left position of a kilometer point
   */
  leftAtStart: (km: number) => number

  /**
   * Center position of a kilometer point
   */
  leftAtCenter: (km: number) => number

  /**
   * Pixel width at a specific kilometer point
   */
  widthAtPk: (km: number) => number

  /**
   * Array of grid positions for snapping (pixels)
   */
  grid: () => number[]

  /**
   * Grid index → kilometer conversion
   */
  kmAtGridIndex: (index: number) => number

  /**
   * Grid indices where a new PK block begins — the seams of a composite axis.
   * The cell at each index is the first of its block, the one before it the last
   * of the previous block, so a renderer can mark the discontinuity and read its
   * two touching PKs straight off `kmAtGridIndex`. A single-block axis has none.
   */
  blockSeams?: () => number[]

  /**
   * The single PK block a section lives in, when both of its ends sit in the
   * SAME block — with the block's own axis (correct direction/bounds/grid) and
   * its pixel offset on the shared ruler. Returns `null` when the ends straddle
   * two blocks (no single reading direction) or fall in a gap.
   *
   * The edit paths read `axis.direction`/`axis.kmStart` as scalars, which a
   * composite axis only answers for its envelope. Resolving the block lets a
   * section wholly inside one block edit against the right scale even while the
   * viewport spans several blocks. A single-block axis leaves this undefined —
   * it already IS the block, so callers fall back to the axis itself.
   */
  resolveEditBlock?: (
    kmStart: number,
    kmEnd: number,
  ) => {axis: Axis; left: number} | null

  /**
   * The PKs at the two screen ends of the axis. The grid only steps in whole
   * `echantillon`, so a block ending on a fractional PK (a sector path's true
   * endpoint) is never a grid cell — the ruler needs this to label its far end
   * instead of stopping at the last round step. Read per block, not off the
   * envelope `kmStart`/`kmEnd`, which a composite reorders.
   */
  endpoints?: () => {leftKm: number; rightKm: number}

  /**
   * The PK blocks this axis chains, each with its pixel slot on the shared ruler
   * and its own single-block axis. Only a composite axis implements it; a single
   * block leaves it undefined (it already IS the block). The ruler draws one
   * sub-ruler per block at the block's own offset, so its labels line up with the
   * km-placed layers (segments/bars) instead of drifting a closing column per
   * seam — a continuous flow ruler drops the interior closing cells the offsets
   * still bank.
   */
  blocks?: () => {left: number; width: number; axis: Axis}[]

  /**
   * Height of the PKWrapper adjusted to the kmMax
   */
  heightPK: number

  /**
   * Check if a kilometer is within the project bounds [kmStart, kmEnd]
   */
  isKmInBounds: (km: number) => boolean

  /**
   * Check if the kmStart → kmEnd order respects the project direction
   */
  isKmOrderValid: (kmStart: number, kmEnd: number) => boolean

  /**
   * Complete validation of a kilometric interval
   * Returns {valid: boolean, errors: ['kmStart' | 'kmEnd']}
   */
  validateKmInterval: (
    kmStart: number,
    kmEnd: number,
  ) => {
    valid: boolean
    errors: ('kmStart' | 'kmEnd')[]
  }
}

/**
 * Normalizes kmStart/kmEnd to ascending order (min/max)
 */
function getKmRange(kmStart: number, kmEnd: number) {
  const kmMin = Math.min(kmStart, kmEnd)
  const kmMax = Math.max(kmStart, kmEnd)
  return {kmMin, kmMax}
}

/**
 * Normalizes interval to ascending order [min, max]
 */
function normalizeInterval(a: number, b: number) {
  return a <= b ? [a, b] : [b, a]
}

/**
 * Creates an axis conversion system for a project.
 * The axis converts railway kilometric coordinates to pixel positions.
 * It abstracts the complexity of the width calculation and the inversion of the axis for the DESC direction.
 *
 * @param kmStart - Starting kilometer of the project
 * @param kmEnd - Ending kilometer of the project
 * @param echantillonValue - Sampling value of the project (space between km points)
 * @param switches - Switches of the project
 * @param stretches - PK intervals held at a minimum pixel width, see `TAxisStretch`
 * @returns Axis object with conversion methods
 */
export function createAxis(
  kmStart: number,
  kmEnd: number,
  echantillonValue: number,
  switches: TSwitch[],
  stretches: TAxisStretch[] = [],
): Axis {
  const direction = kmStart < kmEnd ? 'ASC' : 'DESC'
  const {kmMin, kmMax} = getKmRange(kmStart, kmEnd)
  // Total width always calculated on normalized ascending axis
  const total = calculateWidthSecteur(
    kmMin,
    kmMax,
    switches,
    kmMax,
    echantillonValue,
    stretches,
  )

  const width = (startKm: number, endKm: number) => {
    const [start, end] = normalizeInterval(startKm, endKm)
    if (start === end) return 0
    return calculateWidthSecteur(
      start,
      end,
      switches,
      kmMax,
      echantillonValue,
      stretches,
    )
  }

  // Left position of an interval on screen
  // ASC: ascending distance from kmMin → start
  // DESC: totalWidth - ascending distance from kmMin → end
  const left = (startKm: number, endKm: number) => {
    const [start, end] = normalizeInterval(startKm, endKm)
    const leftAsc = calculateWidthSecteur(
      kmMin,
      start,
      switches,
      kmMax,
      echantillonValue,
      stretches,
    )
    if (direction === 'ASC') return leftAsc

    const leftAscEnd = calculateWidthSecteur(
      kmMin,
      end,
      switches,
      kmMax,
      echantillonValue,
      stretches,
    )
    return total - leftAscEnd
  }

  //
  const widthAtPk = (km: number) => getWidthAtPK(switches, km)

  const leftAtStart = (km: number) => left(km, km)

  const leftAtCenter = (km: number) => leftAtStart(km) + widthAtPk(km) / 2

  // Snapping grid (pixel positions in ascending order on screen)
  const grid = () => {
    // getGridCell returns cumulative positions for ascending axis (0 .. total)
    const asc = getGridCell(kmMin, kmMax, echantillonValue, switches, stretches)
    if (direction === 'ASC') return asc
    const n = asc.length
    const result = new Array(n)
    for (let i = 0; i < n; i++) {
      result[i] = total - asc[n - 1 - i]
    }
    return result
  }

  // Index → km, clamped to the block's km domain: the trailing full-width cell
  // for a partial last stretch (calculateWidthSecteur's kmEnd_project branch)
  // would otherwise read past kmMax (ASC) or before kmMin (DESC).
  const kmAtGridIndex = (index: number) => {
    const step = echantillonValue
    if (direction === 'ASC') return Math.min(kmMin + index * step, kmMax)
    return Math.max(kmMax - index * step, kmMin)
  }

  const heightPK = kmMax >= 100000 ? 46 : kmMax >= 10000 ? 40 : 34

  // Validation methods
  const isKmInBounds = (km: number) => {
    return km >= kmMin && km <= kmMax
  }

  const isKmOrderValid = (start: number, end: number) => {
    return direction === 'ASC' ? start <= end : start >= end
  }

  const validateKmInterval = (start: number, end: number) => {
    const errors: ('kmStart' | 'kmEnd')[] = []

    if (!isKmInBounds(start)) errors.push('kmStart')
    if (!isKmInBounds(end)) errors.push('kmEnd')

    if (errors.length === 0 && !isKmOrderValid(start, end)) {
      errors.push('kmStart', 'kmEnd')
    }

    return {
      valid: errors.length === 0,
      errors,
    }
  }

  // Screen ends: ASC lays kmMin left / kmMax right, DESC the reverse. These are
  // the block's true bounds, which may be fractional PKs off the grid step.
  const endpoints = () => ({
    leftKm: direction === 'ASC' ? kmMin : kmMax,
    rightKm: direction === 'ASC' ? kmMax : kmMin,
  })

  return {
    direction,
    kmStart: kmMin,
    kmEnd: kmMax,
    echantillonValue,
    switches,
    totalWidth: () => total,
    width,
    left,
    leftAtStart,
    leftAtCenter,
    widthAtPk,
    grid,
    kmAtGridIndex,
    endpoints,
    heightPK,
    isKmInBounds,
    isKmOrderValid,
    validateKmInterval,
  }
}
